<script lang="ts">
  /**
   * A room before its game starts: the code to pass on, the seats, what kind
   * of room it is, and the one button that starts it. TwinTen's co-op and
   * versus lobbies; Interlock's duel lobby.
   *
   * Top to bottom: a header with the title (and, while the player may simply
   * step away, a Back chevron: `back`); the code (`LobbyCode`) with a quiet
   * `note` under it (since 1.3.0 `code` is optional: a lobby against the
   * computer leaves it out, the code block and its Copy and Share are simply
   * absent, and from 1024px the lobby is one centred column); the seats (`SeatRow`s) and `afterSeats` (an invite to
   * fill a free seat); the room's `chips`; the game's
   * `rules` (a summary of the deal) and `extras` (a head-to-head, a
   * per-player loadout picker, an invite that belongs below the deal); `primary` (Start, Ready up); the `status`
   * lines ("Waiting for…"); `leave` (Cancel this game, Leave room) in the
   * danger colour; and `after` lines (why a cancel failed, the connection).
   * From 1024px the code and the seats sit side by side.
   *
   * **Server-driven: `dismissible={false}`.** A lobby the server owns must
   * not close on Back, Escape or (as a sheet) the backdrop: each calls
   * `onbackattempt` with what it was instead, and Back is re-armed at once
   * (`BackGuard`), so a second Back lands here again rather than leaving
   * the app. The game answers with its own question (a `ConfirmDialog`:
   * "Leave the room?") and closes the lobby itself. The header draws no
   * chevron then, and `onbackattempt` and `leave` (the way out) are
   * required. A dismissible page lobby leaves Back to the game (its own
   * history entry, `pushOverlay`); the chevron calls `back.onclick`.
   *
   * `presentation="sheet"` draws the same content in a `Sheet` (a dialog
   * from 1024px) over whatever is behind it, for a lobby that opens on top
   * of a screen rather than as one; dismissible, its close button, Escape,
   * Back and the backdrop call `back.onclick`, so `back` is required.
   *
   * Test ids (`testids`, each with a default): `root` `ui-lobby`, `back`
   * `ui-screen-back`, `note` `ui-lobby-note`, `seats` `ui-lobby-seats`,
   * `chips` `ui-lobby-chips`; the code's own (`codeTestids`, see `LobbyCode`);
   * a seat, a chip, a button or a line takes the `testid` it is given.
   */
  import type { Snippet } from 'svelte';

  import { BackGuard } from '../backGuard.js';
  import Chip from '../components/Chip.svelte';
  import LobbyCode from '../components/LobbyCode.svelte';
  import PillButton from '../components/PillButton.svelte';
  import ScreenHeader from '../components/ScreenHeader.svelte';
  import SeatRow from '../components/SeatRow.svelte';
  import Sheet from '../components/Sheet.svelte';
  import TextButton from '../components/TextButton.svelte';
  import type {
    BackAttemptHandler,
    LobbyChip,
    LobbyCodeProps,
    LobbySeat,
    TemplateLine,
  } from './playTypes.js';
  import type { TemplateAction } from '../types.js';

  interface LobbyProps {
    readonly title: string;
    /** The room's code. Absent: a room nobody joins by code (against the computer). Optional since 1.3.0. */
    readonly code?: LobbyCodeProps | undefined;
    readonly seats: readonly LobbySeat[];
    /** Right under the seats: an invite to fill a free one. */
    readonly afterSeats?: Snippet | undefined;
    /** A quiet line under the code ("Saved under this code for 3 days"). */
    readonly note?: string | undefined;
    readonly chips?: readonly LobbyChip[] | undefined;
    readonly rules?: Snippet | undefined;
    readonly extras?: Snippet | undefined;
    readonly primary?: TemplateAction | undefined;
    readonly status?: readonly TemplateLine[] | undefined;
    readonly after?: readonly TemplateLine[] | undefined;
    /** Words for the seats' tags (see `SeatRow`). */
    readonly youLabel?: string | undefined;
    readonly botLabel?: string | undefined;
    readonly readyLabel?: string | undefined;
    readonly notReadyLabel?: string | undefined;
    readonly codeTestids?:
      | {
          readonly card?: string;
          readonly code?: string;
          readonly copy?: string;
          readonly share?: string;
          readonly note?: string;
          readonly link?: string;
        }
      | undefined;
    readonly testids?:
      | {
          readonly root?: string;
          readonly back?: string;
          readonly note?: string;
          readonly seats?: string;
          readonly chips?: string;
        }
      | undefined;
  }

  /** The header's chevron: step away and keep the room. */
  interface LobbyBack {
    readonly label: string;
    readonly onclick: () => void;
  }

  /**
   * Every lobby has a way out, and the type says which:
   *
   * - **locked** (`dismissible: false`, server-driven): Back, Escape and the
   *   backdrop call `onbackattempt`, and `leave` (Leave room) is the way
   *   out, so both are required; there is no chevron;
   * - **a dismissible sheet**: its close button, Escape, Back and the
   *   backdrop all call `back.onclick`, so `back` is required;
   * - **a dismissible page** (the default): `back` draws the chevron; without
   *   it the game owns Back through the page's own history entry.
   */
  type WayOut =
    | {
        readonly dismissible: false;
        readonly onbackattempt: BackAttemptHandler;
        readonly leave: TemplateAction;
        readonly back?: undefined;
        readonly presentation?: 'page' | 'sheet';
      }
    | {
        readonly dismissible?: true;
        readonly presentation: 'sheet';
        readonly back: LobbyBack;
        readonly leave?: TemplateAction | undefined;
        readonly onbackattempt?: undefined;
      }
    | {
        readonly dismissible?: true;
        readonly presentation?: 'page';
        readonly back?: LobbyBack | undefined;
        readonly leave?: TemplateAction | undefined;
        readonly onbackattempt?: undefined;
      };

  type Props = LobbyProps & WayOut;

  const {
    title,
    code,
    seats,
    afterSeats,
    note,
    chips = [],
    rules,
    extras,
    primary,
    status = [],
    leave,
    after = [],
    back,
    dismissible = true,
    onbackattempt,
    presentation = 'page',
    youLabel,
    botLabel,
    readyLabel,
    notReadyLabel,
    codeTestids,
    testids,
  }: Props = $props();

  const locked = $derived(dismissible === false);
  const asPage = $derived(presentation === 'page');

  // A page that may not be left: hold a history entry of our own while it
  // is up, and answer a Back with the game's question, re-armed at once.
  $effect(() => {
    if (!asPage || !locked) return undefined;
    const guard: BackGuard = new BackGuard((asking) => {
      if (!asking) return;
      onbackattempt?.('back');
      guard.keep();
    });
    guard.setLive(true);
    return () => guard.dispose();
  });

  function onKeydown(event: KeyboardEvent): void {
    if (!asPage || !locked || event.key !== 'Escape' || event.defaultPrevented) return;
    event.preventDefault();
    onbackattempt?.('escape');
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#snippet body()}
  <div class="ui-lobby-hero" class:ui-lobby-hero--after={afterSeats !== undefined}>
    {#if code !== undefined}
      <div class="ui-lobby-code-cell">
        <LobbyCode {...code} testids={codeTestids} />
      </div>
    {/if}
    {#if note !== undefined}
      <p class="ui-lobby-note" data-testid={testids?.note ?? 'ui-lobby-note'}>{note}</p>
    {/if}
    <ul class="ui-lobby-seats" data-testid={testids?.seats ?? 'ui-lobby-seats'}>
      {#each seats as seat (seat.key)}
        <SeatRow
          name={seat.name}
          you={seat.you}
          connected={seat.connected}
          ready={seat.ready}
          bot={seat.bot}
          badge={seat.badge}
          empty={seat.empty}
          avatar={seat.avatar}
          onopen={seat.onopen}
          openLabel={seat.openLabel}
          openTestid={seat.openTestid}
          testid={seat.testid}
          {youLabel}
          {botLabel}
          {readyLabel}
          {notReadyLabel}
        />
      {/each}
    </ul>
    {#if afterSeats !== undefined}
      <div class="ui-lobby-after-seats">{@render afterSeats()}</div>
    {/if}
    {#if chips.length > 0}
      <p class="ui-lobby-chips" data-testid={testids?.chips ?? 'ui-lobby-chips'}>
        {#each chips as chip (chip.key)}
          <Chip tone={chip.tone ?? 'sunken'} testid={chip.testid}>{chip.label}</Chip>
        {/each}
      </p>
    {/if}
  </div>

  {#if rules !== undefined}{@render rules()}{/if}
  {#if extras !== undefined}{@render extras()}{/if}

  {#if primary !== undefined}
    <PillButton
      label={primary.label}
      onclick={primary.onclick}
      disabled={primary.disabled ?? false}
      busy={primary.busy ?? false}
      icon={primary.icon}
      size="lg"
      testid={primary.testid}
    />
  {/if}
  {@render lines(status)}
  {#if leave !== undefined}
    <div class="ui-lobby-leave">
      <TextButton
        label={leave.label}
        onclick={leave.onclick}
        tone="danger"
        disabled={(leave.disabled ?? false) || (leave.busy ?? false)}
        icon={leave.icon}
        testid={leave.testid}
      />
    </div>
  {/if}
  {@render lines(after)}
{/snippet}

{#snippet lines(list: readonly TemplateLine[])}
  {#each list as line, index (index)}
    <p
      class="ui-lobby-line"
      class:ui-lobby-line--danger={line.tone === 'danger'}
      role={line.role ?? 'status'}
      data-testid={line.testid}
    >
      {line.text}
    </p>
  {/each}
{/snippet}

{#if asPage}
  <div class="ui-lobby" data-testid={testids?.root ?? 'ui-lobby'}>
    {#if back !== undefined && !locked}
      <ScreenHeader
        {title}
        backLabel={back.label}
        onback={back.onclick}
        safeArea
        backTestid={testids?.back ?? 'ui-screen-back'}
      />
    {:else}
      <header class="ui-lobby-header">
        <h1 class="ui-lobby-title">{title}</h1>
      </header>
    {/if}
    <div class="ui-lobby-body" class:ui-lobby-body--single={code === undefined}>
      {@render body()}
    </div>
  </div>
{:else}
  <Sheet
    open
    {title}
    {dismissible}
    {onbackattempt}
    onclose={() => back?.onclick()}
    gap="md"
    testid={testids?.root ?? 'ui-lobby'}
  >
    <div class="ui-lobby-body ui-lobby-body--sheet">{@render body()}</div>
  </Sheet>
{/if}

<style>
  .ui-lobby {
    box-sizing: border-box;
    height: 100svh;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-lobby-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 2.75rem;
    padding: calc(0.5rem + env(safe-area-inset-top)) 1rem 0.5rem;
  }

  .ui-lobby-title {
    margin: 0;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
  }

  .ui-lobby-body {
    box-sizing: border-box;
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: safe center;
    gap: 1.25rem;
    padding: 1rem 1.25rem calc(2rem + env(safe-area-inset-bottom));
  }

  .ui-lobby-body--sheet {
    padding: 0;
  }

  /* The code, the seats and the chips as one unit: no gap wide enough to
     read as dead space between the things this screen is for. */
  .ui-lobby-hero {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .ui-lobby-note {
    margin: 0;
    text-align: center;
    font: var(--ui-text-caption);
    font-weight: 400;
    line-height: 1.4;
    color: var(--ui-ink-2);
  }

  .ui-lobby-seats {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .ui-lobby-after-seats {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .ui-lobby-chips {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.35rem;
  }

  .ui-lobby-leave {
    display: flex;
    justify-content: center;
  }

  .ui-lobby-line {
    margin: 0;
    text-align: center;
    font: var(--ui-text-label);
    color: var(--ui-ink-2);
  }

  .ui-lobby-line--danger {
    color: var(--ui-danger);
  }

  @media (min-width: 1024px) {
    .ui-lobby-body:not(.ui-lobby-body--sheet) {
      max-width: 48rem;
      margin-inline: auto;
    }

    /* No code (since 1.3.0): nothing to set beside the seats, so the phone's
       one column, centred, rather than seats stretched across both. */
    .ui-lobby-body--single:not(.ui-lobby-body--sheet) {
      max-width: 30rem;
    }

    /* The code (and its note) on the left, the seats and chips on the
       right, each placed by row as well as column: auto-placement would put
       the seats beside the note instead of beside the code. */
    .ui-lobby-body:not(.ui-lobby-body--sheet, .ui-lobby-body--single) .ui-lobby-hero {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 1.5rem;
      row-gap: 0.75rem;
      align-items: start;
    }

    .ui-lobby-body:not(.ui-lobby-body--sheet, .ui-lobby-body--single) .ui-lobby-code-cell {
      grid-column: 1;
      grid-row: 1;
    }

    .ui-lobby-body:not(.ui-lobby-body--sheet, .ui-lobby-body--single) .ui-lobby-note {
      grid-column: 1;
      grid-row: 2;
    }

    .ui-lobby-body:not(.ui-lobby-body--sheet, .ui-lobby-body--single) .ui-lobby-seats {
      grid-column: 2;
      grid-row: 1;
      align-self: center;
    }

    .ui-lobby-body:not(.ui-lobby-body--sheet, .ui-lobby-body--single) .ui-lobby-chips {
      grid-column: 2;
      grid-row: 2;
      justify-content: flex-start;
    }

    /* An invite under the seats, and the chips under it. */
    .ui-lobby-body:not(.ui-lobby-body--sheet, .ui-lobby-body--single) .ui-lobby-after-seats {
      grid-column: 2;
      grid-row: 2;
    }

    .ui-lobby-body:not(.ui-lobby-body--sheet, .ui-lobby-body--single)
      .ui-lobby-hero--after
      .ui-lobby-chips {
      grid-row: 3;
    }
  }
</style>
